import { useReducer, useCallback, useRef, useState, useEffect } from "react";
import type {
  TimelineProject,
  TimelineAction,
  TimelineTextOverlay,
  TimelineHistory,
  FilterState,
  TimelineClip,
} from "@scroll/shared-types/timeline";
import { timelineReducer, createTimelineHistory } from "@scroll/shared-types/timeline-reducer";
import type { TimelineTransition } from "@scroll/shared-types/timeline";
import {
  getTotalDuration,
  timeToClip,
  localTimeToSourceTime,
  getActiveOverlays,
  getActiveTransition,
  interpolateVolume,
} from "@scroll/shared-types/timeline-utils";
import type {
  LocalBlobMap,
  TimelineDraftService,
} from "@/lib/timeline-drafts";
import { TimelineDraftQuotaError } from "@/lib/timeline-drafts";
import { cancelUploadedAudio, cancelUploadedClip } from "@/lib/timeline-upload";

const AUTOSAVE_DEBOUNCE = 1000;

export interface TimelineAutosaveOptions {
  service: TimelineDraftService;
  /** Returns the current autosave config, or null to skip this save (e.g. user not ready). */
  getConfig: () => { contextKey: string; userId: string; localBlobs?: LocalBlobMap } | null;
  /** Called once when a save is rejected by storage quota. The editor keeps running. */
  onQuotaError?: () => void;
}

export interface TimelineEditorOptions {
  /** Max clip count from capabilities; 0/undefined = unlimited. Gates split. */
  maxClips?: number;
}

export function useTimelineEditor(
  initialProject: TimelineProject,
  autosave?: TimelineAutosaveOptions,
  options?: TimelineEditorOptions,
) {
  const [history, rawDispatch] = useReducer(
    timelineReducer,
    initialProject,
    createTimelineHistory,
  );

  const project = history.present;
  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  // ─── Playback State ────────────────────────────────────────────────

  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);
  const [activeClipIndex, setActiveClipIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const loadedClipIdRef = useRef<string | null>(null);
  const rafRef = useRef<number>(0);
  const playStartRef = useRef<{ wallTime: number; timelineTime: number } | null>(null);

  const totalDuration = getTotalDuration(project);

  // ─── Refs for stable callbacks ─────────────────────────────────────
  // Avoid stale closures in RAF loop and keyboard shortcuts

  const totalDurationRef = useRef(totalDuration);
  useEffect(() => { totalDurationRef.current = totalDuration; }, [totalDuration]);

  const projectRef = useRef(project);
  useEffect(() => { projectRef.current = project; }, [project]);

  // ─── Playback Logic ────────────────────────────────────────────────

  const activeClipIndexRef = useRef(activeClipIndex);
  useEffect(() => { activeClipIndexRef.current = activeClipIndex; }, [activeClipIndex]);

  const isPlayingRef = useRef(isPlaying);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  const maxClipsRef = useRef(options?.maxClips ?? 0);
  useEffect(() => { maxClipsRef.current = options?.maxClips ?? 0; }, [options?.maxClips]);

  // ─── Deferred upload-blob cleanup ──────────────────────────────────
  // Deleting a clip/audio track must NOT delete its uploaded blob right
  // away: the undo stack can restore the item, and a restored item whose
  // blob is gone plays nothing in the preview and fails the server render
  // on publish. The dispatch wrapper below queues the orphaned blob key for
  // EVERY delete action — owning this here (rather than in a component
  // wrapper) means no dispatch path can bypass it. flushPendingBlobCleanup
  // releases unreferenced keys once undo can no longer resurrect them
  // (export/close/unmount); the daily 03:00 UTC server sweep is the
  // backstop if the tab dies first.

  const pendingBlobCleanupRef = useRef<Map<string, "clip" | "audio">>(new Map());
  // Set once the autosave machinery is wired up (below). flushPendingBlobCleanup
  // calls it to persist the post-deletion project BEFORE deleting blobs, so a
  // close within the autosave debounce window can't leave the saved draft
  // pointing at a blob we're about to delete.
  const persistDraftNowRef = useRef<() => void>(() => {});

  const dispatch = useCallback((action: TimelineAction) => {
    if (action.type === "DELETE_CLIP") {
      const clip = projectRef.current.clips.find((c) => c.id === action.clipId);
      if (clip?.blobKey) pendingBlobCleanupRef.current.set(clip.blobKey, "clip");
    } else if (action.type === "DELETE_AUDIO_TRACK") {
      const track = projectRef.current.audioTracks.find((t) => t.id === action.id);
      if (track?.blobKey) pendingBlobCleanupRef.current.set(track.blobKey, "audio");
    }
    rawDispatch(action);
  }, []);

  const flushPendingBlobCleanup = useCallback(() => {
    const pending = pendingBlobCleanupRef.current;
    if (pending.size === 0) return;

    // Persist the current (post-deletion) project first. The debounced
    // autosave may not have fired yet — without this, closing the editor
    // within AUTOSAVE_DEBOUNCE leaves the saved draft referencing a blob we
    // delete just below, so resuming the draft 404s on that source.
    persistDraftNowRef.current();

    const proj = projectRef.current;
    const liveKeys = new Set(
      [
        ...proj.clips.map((c) => c.blobKey),
        ...proj.audioTracks.map((t) => t.blobKey),
      ].filter((k): k is string => !!k),
    );

    for (const [key, kind] of pending) {
      if (liveKeys.has(key)) continue; // restored via undo, or shared by a split sibling
      if (kind === "clip") void cancelUploadedClip(key);
      else void cancelUploadedAudio(key);
    }
    pending.clear();
  }, []);

  // Last-resort flush on unmount: the editor can be torn down without its
  // close/export handlers running (parent modal sets isOpen=false, route
  // change, error boundary). Pending keys would otherwise leak until the
  // daily sweep.
  useEffect(() => () => { flushPendingBlobCleanup(); }, [flushPendingBlobCleanup]);

  // ─── Audio Track Elements ──────────────────────────────────────────

  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const audioTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  /**
   * Seek/start all audio track elements to match a timeline position.
   * Shared by the play/pause effect and mid-playback seeks — without the
   * seek path, audio keeps playing from its old position and drifts from
   * the video after any scrub during playback.
   */
  const syncAudioPlayback = useCallback((startTime: number) => {
    const audioEls = audioElementsRef.current;

    for (const t of audioTimeoutsRef.current) clearTimeout(t);
    audioTimeoutsRef.current = [];

    const proj = projectRef.current;
    for (const track of proj.audioTracks) {
      const trackDuration = track.trimEnd - track.trimStart;
      const trackEnd = track.timelineStart + trackDuration;

      // Track is already finished at this position — silence it (it may
      // still be playing after a forward seek).
      if (startTime >= trackEnd) {
        audioEls.get(track.id)?.pause();
        continue;
      }

      let audio = audioEls.get(track.id);
      if (!audio) {
        audio = document.createElement("audio");
        audio.src = track.sourceUri;
        audio.preload = "auto";
        audioEls.set(track.id, audio);
      }

      // Calculate where in the source audio we should be
      const timeIntoTrack = Math.max(0, startTime - track.timelineStart);
      audio.currentTime = track.trimStart + timeIntoTrack;
      audio.volume = interpolateVolume(track.volumeKeyframes, timeIntoTrack);

      // Delay start if track hasn't begun yet
      const delay = Math.max(0, track.timelineStart - startTime);
      if (delay > 0) {
        audio.pause();
        const handle = setTimeout(() => {
          if (audioEls.has(track.id)) {
            audio!.play().catch(() => {});
          }
        }, delay * 1000);
        audioTimeoutsRef.current.push(handle);
      } else {
        audio.play().catch(() => {});
      }
    }
  }, []);

  /** Load a clip into the video element, switching source if needed */
  const loadClipIntoVideo = useCallback(
    (clip: TimelineProject["clips"][0], localTime: number) => {
      const video = videoRef.current;
      if (!video || clip.type !== "video") return;

      if (loadedClipIdRef.current !== clip.id) {
        video.src = clip.sourceUri;
        video.load();
        loadedClipIdRef.current = clip.id;
      }

      video.currentTime = localTimeToSourceTime(clip, localTime);
      video.playbackRate = clip.speed;
      video.volume = clip.volume;
    },
    [],
  );

  const seekTo = useCallback(
    (time: number) => {
      const clamped = Math.max(0, Math.min(time, totalDurationRef.current));
      setCurrentTime(clamped);

      // Mid-playback seek: rebase the wall-clock anchor so the rAF loop
      // continues from the new position instead of snapping back to the
      // pre-seek trajectory, and re-seek the audio tracks (they only
      // auto-sync on play/pause transitions).
      const wasPlaying = isPlayingRef.current && playStartRef.current !== null;
      if (wasPlaying) {
        playStartRef.current = {
          wallTime: performance.now(),
          timelineTime: clamped,
        };
        syncAudioPlayback(clamped);
      }

      const proj = projectRef.current;
      const hit = timeToClip(proj, clamped);
      if (!hit) return;

      if (hit.clipIndex !== activeClipIndexRef.current) {
        setActiveClipIndex(hit.clipIndex);
      }

      loadClipIntoVideo(proj.clips[hit.clipIndex], hit.localTime);
      if (wasPlaying) {
        if (proj.clips[hit.clipIndex].type === "video") {
          videoRef.current?.play().catch(() => {});
        } else {
          // Landed on an image clip: the video element is merely hidden by
          // the preview, so without an explicit pause the previous clip's
          // audio keeps playing under the image.
          videoRef.current?.pause();
        }
      }
    },
    [loadClipIntoVideo, syncAudioPlayback],
  );

  const play = useCallback(() => {
    const dur = totalDurationRef.current;
    const startTime = currentTime >= dur ? 0 : currentTime;
    if (currentTime >= dur) {
      seekTo(0);
    }
    setIsPlaying(true);
    playStartRef.current = {
      wallTime: performance.now(),
      timelineTime: startTime,
    };

    const proj = projectRef.current;
    const hit = timeToClip(proj, startTime);
    if (hit) {
      setActiveClipIndex(hit.clipIndex);
      loadClipIntoVideo(proj.clips[hit.clipIndex], hit.localTime);
      videoRef.current?.play();
    }
  }, [currentTime, seekTo, loadClipIntoVideo]);

  const pause = useCallback(() => {
    setIsPlaying(false);
    playStartRef.current = null;
    cancelAnimationFrame(rafRef.current);

    const video = videoRef.current;
    if (video) video.pause();
  }, []);

  const togglePlayback = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  // Stable refs (avoids re-attaching keyboard listener 60x/sec)
  const togglePlaybackRef = useRef(togglePlayback);
  useEffect(() => { togglePlaybackRef.current = togglePlayback; }, [togglePlayback]);

  const currentTimeRef = useRef(currentTime);
  useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);

  const selectedClipIdRef = useRef(selectedClipId);
  useEffect(() => { selectedClipIdRef.current = selectedClipId; }, [selectedClipId]);

  const selectedOverlayIdRef = useRef(selectedOverlayId);
  useEffect(() => { selectedOverlayIdRef.current = selectedOverlayId; }, [selectedOverlayId]);

  // Animation frame loop for playback
  useEffect(() => {
    cancelAnimationFrame(rafRef.current); // cancel any stale frame
    if (!isPlaying || !playStartRef.current) return;

    const tick = () => {
      if (!playStartRef.current) return;

      const elapsed = (performance.now() - playStartRef.current.wallTime) / 1000;
      const newTime = playStartRef.current.timelineTime + elapsed;

      if (newTime >= totalDurationRef.current) {
        setCurrentTime(totalDurationRef.current);
        setIsPlaying(false);
        playStartRef.current = null;
        const video = videoRef.current;
        if (video) video.pause();
        return;
      }

      setCurrentTime(newTime);

      // Detect clip boundary crossing and switch video source
      const proj = projectRef.current;
      const hit = timeToClip(proj, newTime);
      if (hit && hit.clipIndex !== activeClipIndexRef.current) {
        setActiveClipIndex(hit.clipIndex);
        const clip = proj.clips[hit.clipIndex];
        if (clip.type === "video") {
          loadClipIntoVideo(clip, hit.localTime);
          videoRef.current?.play();
        } else if (videoRef.current) {
          videoRef.current.pause();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, loadClipIntoVideo]);

  // ─── Audio Track Preview ──────────────────────────────────────────
  // Start/stop audio tracks when playback state changes. Mid-playback
  // seeks re-sync directly via syncAudioPlayback inside seekTo.

  useEffect(() => {
    if (isPlaying && playStartRef.current) {
      syncAudioPlayback(playStartRef.current.timelineTime);
    }
    // The cleanup below runs on every isPlaying flip (and unmount), so it
    // is the single stop path — no else branch needed.
    return () => {
      for (const t of audioTimeoutsRef.current) clearTimeout(t);
      audioTimeoutsRef.current = [];
      for (const audio of audioElementsRef.current.values()) {
        audio.pause();
      }
    };
  }, [isPlaying, syncAudioPlayback]);

  // Update audio track volumes based on keyframes during playback
  useEffect(() => {
    if (!isPlaying) return;

    const audioEls = audioElementsRef.current;
    const proj = projectRef.current;

    for (const track of proj.audioTracks) {
      const audio = audioEls.get(track.id);
      if (!audio || audio.paused) continue;

      const timeIntoTrack = currentTime - track.timelineStart;
      const trackDuration = track.trimEnd - track.trimStart;

      if (timeIntoTrack < 0 || timeIntoTrack >= trackDuration) {
        audio.pause();
        continue;
      }

      audio.volume = interpolateVolume(track.volumeKeyframes, timeIntoTrack);
    }
  }, [currentTime, isPlaying]);

  // Clean up audio elements when tracks change
  useEffect(() => {
    const audioEls = audioElementsRef.current;
    const currentIds = new Set(project.audioTracks.map((t) => t.id));

    for (const [id, audio] of audioEls) {
      if (!currentIds.has(id)) {
        audio.pause();
        audio.src = "";
        audioEls.delete(id);
      }
    }
  }, [project.audioTracks]);

  // ─── Initialize video with first clip's source ────────────────────

  useEffect(() => {
    const video = videoRef.current;
    const clip = project.clips[0];
    if (video && clip?.type === "video" && !loadedClipIdRef.current) {
      video.src = clip.sourceUri;
      loadedClipIdRef.current = clip.id;
    }
  }, []);

  // ─── Sync activeClipIndex when clips are added/removed/reordered ───
  // Keyed on the clip ID sequence, NOT the clips array identity: identity
  // changes on every TRIM_CLIP_PREVIEW (per pointermove during a trim
  // drag), and reacting to those swaps video.src each time the dragged
  // boundary flaps across the playhead. The ID sequence only changes on
  // delete/add/split/reorder — exactly the edits that can move a different
  // clip under the playhead. Reads through refs so values are never stale.

  const clipOrderKey = project.clips.map((c) => c.id).join("|");

  useEffect(() => {
    const proj = projectRef.current;
    const hit = timeToClip(proj, currentTimeRef.current);
    if (hit && hit.clipIndex !== activeClipIndexRef.current) {
      setActiveClipIndex(hit.clipIndex);
      loadClipIntoVideo(proj.clips[hit.clipIndex], hit.localTime);
      // loadClipIntoVideo leaves the element paused after a src swap; if
      // this resync happened mid-playback (clip deleted/reordered under
      // the playhead), resume — the rAF boundary branch can't, because
      // activeClipIndexRef is already up to date by its next tick.
      if (isPlayingRef.current) {
        if (proj.clips[hit.clipIndex].type === "video") {
          videoRef.current?.play().catch(() => {});
        } else {
          videoRef.current?.pause();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipOrderKey, loadClipIntoVideo]);

  // ─── Active Overlays + Selected Clip ───────────────────────────────

  const activeOverlays = getActiveOverlays(project.textOverlays, currentTime);

  const selectedClip = selectedClipId
    ? project.clips.find((c) => c.id === selectedClipId) ?? null
    : null;

  // Active clip info (under the playhead)
  const activeClip = project.clips[activeClipIndex] ?? project.clips[0] ?? null;
  const activeFilter: FilterState | null = activeClip?.filter ?? null;

  // Active transition (if playhead is in a transition zone). Surface the
  // incoming clip too so VideoPreviewPanel can render a real two-layer
  // preview (crossfade blends two videos, slides reveal the incoming clip
  // from off-screen, etc.) instead of CSS-only effects on the outgoing clip.
  const activeTransitionInfo = getActiveTransition(project, currentTime);
  const activeTransition: {
    transition: TimelineTransition;
    progress: number;
    incomingClip: TimelineClip | null;
  } | null = activeTransitionInfo
    ? {
        transition: activeTransitionInfo.transition,
        progress: activeTransitionInfo.progress,
        incomingClip: project.clips[activeTransitionInfo.incomingClipIndex] ?? null,
      }
    : null;

  // ─── Auto-save ─────────────────────────────────────────────────────
  // When autosave options are provided, debounce TimelineDraftService.saveDraft
  // on every project change. The caller decides when a save is appropriate
  // (e.g. only after a userId + source-file context key is built).

  const autosaveRef = useRef(autosave);
  useEffect(() => { autosaveRef.current = autosave; }, [autosave]);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const quotaReportedRef = useRef(false);
  // Exposed so the toolbar can render a "Saved Xs ago" affordance. null until
  // the first successful autosave; "saving" mid-write so the UI can flicker a
  // spinner if it wants to.
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [isAutosaving, setIsAutosaving] = useState(false);

  useEffect(() => {
    const opts = autosaveRef.current;
    if (!opts) return;

    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const config = opts.getConfig();
      if (!config) return;
      setIsAutosaving(true);
      opts.service
        .saveDraft({
          contextKey: config.contextKey,
          userId: config.userId,
          project,
          localBlobs: config.localBlobs,
        })
        .then(() => {
          setLastSavedAt(Date.now());
        })
        .catch((err) => {
          if (err instanceof TimelineDraftQuotaError) {
            if (!quotaReportedRef.current) {
              quotaReportedRef.current = true;
              opts.onQuotaError?.();
            }
            return;
          }
          // Non-fatal: editor keeps running even if autosave fails.
          console.warn("[timeline] draft autosave failed", err);
        })
        .finally(() => {
          setIsAutosaving(false);
        });
    }, AUTOSAVE_DEBOUNCE);

    return () => clearTimeout(saveTimeoutRef.current);
  }, [project]);

  // Immediate, fire-and-forget draft persist used by flushPendingBlobCleanup.
  // Cancels the pending debounce and writes the CURRENT project so blob
  // cleanup can never orphan a blob the last-saved draft still references.
  // Reads only refs, so its identity is stable; no setState (it can run
  // during unmount, where a state update would warn).
  useEffect(() => {
    persistDraftNowRef.current = () => {
      const opts = autosaveRef.current;
      if (!opts) return;
      const config = opts.getConfig();
      if (!config) return;
      clearTimeout(saveTimeoutRef.current);
      void opts.service
        .saveDraft({
          contextKey: config.contextKey,
          userId: config.userId,
          project: projectRef.current,
          localBlobs: config.localBlobs,
        })
        .catch(() => {});
    };
  }, []);

  // ─── Dev fixture seek hook ─────────────────────────────────────────
  // Exposes window.__fixtureSeek(seconds) so the Playwright-based fixture
  // harness (scripts/timeline-fixtures/preview-capture.ts) can drive the
  // editor to fixed timestamps before screenshotting. Stripped from prod
  // builds by Vite's dead-code elimination of `import.meta.env.DEV`.

  const seekToRef = useRef(seekTo);
  useEffect(() => { seekToRef.current = seekTo; }, [seekTo]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (typeof window === "undefined") return;
    const w = window as unknown as { __fixtureSeek?: (s: number) => void };
    w.__fixtureSeek = (seconds: number) => seekToRef.current(seconds);
    return () => {
      if (w.__fixtureSeek) delete w.__fixtureSeek;
    };
  }, []);

  // ─── Keyboard Shortcuts ────────────────────────────────────────────
  // Uses refs so the listener is attached once, not re-attached every frame

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if typing in an input or contenteditable
      const el = e.target as HTMLElement;
      if (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable) return;

      if (e.key === " ") {
        e.preventDefault();
        togglePlaybackRef.current();
      } else if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        if (e.shiftKey) {
          dispatch({ type: "REDO" });
        } else {
          dispatch({ type: "UNDO" });
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "y") {
        e.preventDefault();
        dispatch({ type: "REDO" });
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        // Frame-stepping is a paused-mode operation. Pausing first matches
        // pointer scrubbing (which pauses on scrub start) and stops OS key
        // repeat from thrashing audio/video element seeks at repeat rate.
        if (isPlayingRef.current) pause();
        const step = e.shiftKey ? 1 : 1 / 30;
        const direction = e.key === "ArrowRight" ? 1 : -1;
        seekToRef.current(currentTimeRef.current + step * direction);
      } else if (e.key === "s" || e.key === "S") {
        // Split at playhead (gated on the capability clip cap — the
        // serializer truncates anything past maxClips at publish, so
        // letting the split through would silently lose content)
        const proj = projectRef.current;
        const max = maxClipsRef.current;
        if (max > 0 && proj.clips.length >= max) return;
        const hit = timeToClip(proj, currentTimeRef.current);
        if (hit && proj.clips[hit.clipIndex].type === "video") {
          dispatch({
            type: "SPLIT_CLIP",
            clipId: proj.clips[hit.clipIndex].id,
            atTime: hit.localTime,
          });
        }
      } else if (e.key === "Delete" || e.key === "Backspace") {
        // Window-level so deletion works even when focus has escaped the
        // editor shell (e.g. a drawer's close button unmounted and focus
        // fell to document.body). Blob cleanup is safe here: the hook's
        // own dispatch queues orphaned keys for every delete action.
        if (selectedOverlayIdRef.current) {
          e.preventDefault();
          dispatch({ type: "DELETE_TEXT_OVERLAY", id: selectedOverlayIdRef.current });
          setSelectedOverlayId(null);
        } else if (selectedClipIdRef.current && projectRef.current.clips.length > 1) {
          e.preventDefault();
          dispatch({ type: "DELETE_CLIP", clipId: selectedClipIdRef.current });
          setSelectedClipId(null);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dispatch, pause]);

  return {
    // State
    project,
    history,
    currentTime,
    isPlaying,
    totalDuration,
    selectedClipId,
    selectedOverlayId,
    activeOverlays,
    activeFilter,
    activeClip,
    activeClipIndex,
    activeTransition,
    selectedClip,
    canUndo,
    canRedo,
    videoRef,
    lastSavedAt,
    isAutosaving,

    // Actions
    dispatch,
    flushPendingBlobCleanup,
    seekTo,
    setCurrentTime,
    play,
    pause,
    togglePlayback,
    setSelectedClipId,
    setSelectedOverlayId,
  };
}
